<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-changed="$_queryHandler_hasQueryChanged"
            :query-count="$_queryHandler_numberOfQueries"
            default-open
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" outlined hide-details />
            </template>
            <template #default>
              <v-row>
                <v-col v-for="option in filterOptionsToShow" :key="option.name">
                  <autocomplete-input
                    v-model="query.params[option.model]"
                    :clearable="option.clearable"
                    :disabled="hasPageErrors"
                    :items="filters[option.dataKey]"
                    :label="option.name"
                    :loading="isPageLoading"
                    hide-details
                    outlined
                  />
                </v-col>
              </v-row>
            </template>
            <template #content>
              <property-error-table
                :errors="missingPropertiesArray"
                :query="query"
                :search="query.searchTerm"
                :type="type"
              />
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>
  </page-loader>
</template>

<script>
// Helpers
import { mapGetters, mapMutations } from 'vuex'
// Components
import AutocompleteInput from '@/components/forms/autocomplete-input'
import FilterAccordion from '@/components/filter-accordion'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'

import PropertyErrorTable from './components/property-error-table'
// Services
import { languages, offers, partners } from '@/services/offers'
// Mixins
import MissingProperties from '@/mixins/missing-properties'
import QueryParamsHandler from '@/mixins/query-params-handler'

export default {
  name: 'MissingProperties',
  components: {
    AutocompleteInput,
    FilterAccordion,
    PageLoader,
    PropertyErrorTable,
    SearchInput,
  },
  mixins: [MissingProperties, QueryParamsHandler],
  data: () => ({
    isLoading: false,
    filterOptions: [
      {
        name: 'Currency',
        clearable: true,
        dataKey: 'mainCurrencies',
        model: 'currency',
      },
      {
        name: 'Language',
        clearable: true,
        filter: arr => arr.filter(({ key }) => this.properties.map(({ key }) => key).includes(key)),
        dataKey: 'translationLanguages',
        model: 'language',
      },
      {
        name: 'Offer Type',
        clearable: false,
        dataKey: 'offerTypes',
        model: 'offerType',
      },
      {
        name: 'Property',
        clearable: true,
        dataKey: 'properties',
        model: 'property',
      },
    ],
    filterList: {
      offer: ['Currency', 'Language', 'Offer Type', 'Property'],
      partner: ['Currency', 'Language', 'Property'],
    },
    items: [],
    languageOptions: [],
    type: 'partner',
    queriesDefined: ['currency', 'language', 'offerType', 'property'],
    queryDefaults: ['offerType'],
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors', 'isPageLoading']),
    ...mapGetters('offers', ['allOfferTypeSchemas']),
    ...mapGetters('options', ['options', 'hasOptions']),
    ...mapGetters('partners', ['partnerSchema']),
    currentSchema() {
      return this.getSchema(this.type, this.query.params.offerType)
    },
    propertiesToCheck() {
      return this.getPropertiesToCheck(this.currentSchema)
    },
    missingPropertiesArray() {
      return this.mapMissingProperties(this.currentSchema, this.items, {
        hideDrafts: true,
        offerType: this.type === 'offer' ? this.query.params.offerType : undefined,
        selections: this.query.params,
      })
    },
    totalMissingProperties() {
      return this.missingPropertiesArray.reduce(this.countErrors, 0)
    },
    filters() {
      const { mainCurrencies, offerTypes, translationLanguages } = this.options
      return {
        mainCurrencies,
        offerTypes,
        properties: this.propertiesToCheck,
        translationLanguages: this.languageOptions,
      }
    },
    filterOptionsToShow() {
      return this.filterOptions.filter(({ name }) => this.filterList[this.type].includes(name))
    },
  },
  async created() {
    await Promise.all([this.getRelevantData(this.$route.params.type), this.fetchLanguageOptions()])
    this.setPageLoading(false)
  },
  async beforeRouteUpdate(to, from, next) {
    const { type } = to.params
    await this.getRelevantData(type)
    next()
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async fetchLanguageOptions() {
      this.isLoading = true
      try {
        this.languageOptions = (await languages.getLanguages()).map(({ name, code }) => ({ key: name, value: code }))
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch translation language options',
          duration: -1,
          data: { error },
        })
      }
      this.isLoading = false
    },
    async getRelevantData(type) {
      this.isLoading = true
      this.items = []
      this.type = type

      try {
        const getAllItems = this.type === 'offer' ? offers.getAllOffers : partners.getAllPartners
        const { data } = await getAllItems()
        this.items = data
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: `Unable to retrieve ${this.type}`,
        })
      }
      this.isLoading = false
    },
  },
}
</script>
